import * as yup from "yup";
import RequisitionType from "../../Services/Requisition/RequisitionTypeService";
import UserManagementService from "../../Services/UserManagement/UserManagementService";
import { GetSingleElementFromArray } from "./CommonMethods";
import { getFieldValueForValidation } from "./panelSelection";

const stripDNone = (displayType: string) =>
  (displayType || "").replace(/d-none/g, "").replace(/\s+/g, " ").trim();

const ensureDNone = (displayType: string) => {
  const normalized = (displayType || "").replace(/\s+/g, " ").trim();
  return normalized.includes("d-none")
    ? normalized
    : `${normalized} d-none`.trim();
};

const repeatHideDepControls = (
  clonedArray: any,
  index: number,
  id: string,
  name: string,
  fieldIndex: number
) => {
  const repeatField = clonedArray[index]?.fields[fieldIndex];
  repeatField?.repeatDependencyControls
    ?.filter((item: any) => item?.optionID != id && item?.name == name)
    ?.forEach((control: any) => {
      control.dependecyFields?.forEach((depField: any) => {
        depField.displayType = ensureDNone(depField.displayType);
      });
    });
};

const isRepeatField = (field: any) =>
  field?.uiType === "Repeat" || field?.uiTypeId === 33;

/** Restore repeat block field/dependency definitions from API snapshot before re-applying patient data. */
const resetRepeatFieldFromState = (repeatField: any) => {
  if (!repeatField) return repeatField;

  if (repeatField.repeatFieldsState?.length) {
    repeatField.repeatFields = JSON.parse(
      JSON.stringify(repeatField.repeatFieldsState)
    );
  }
  if (repeatField.repeatDependencyControlsState?.length) {
    repeatField.repeatDependencyControls = JSON.parse(
      JSON.stringify(repeatField.repeatDependencyControlsState)
    );
  }

  return repeatField;
};

const applySectionLevelRepeatFields = (
  section: any,
  repeatFieldIndex: number,
  selectedOptionId: string | number,
  triggerName: string
) => {
  const repeatField = section?.fields?.[repeatFieldIndex];
  if (!repeatField?.repeatDependencyControls || !triggerName) return;

  const selectedControl = repeatField.repeatDependencyControls.find(
    (item: any) => item?.optionID == selectedOptionId && item?.name === triggerName
  );
  if (!selectedControl) return;

  const action = (selectedControl.dependencyAction ?? "").toLowerCase();
  const depControlIds = new Set(
    selectedControl.dependecyFields?.map((f: any) => f.controlId) ?? []
  );

  const hideManagedControlIds = new Set<number>();
  repeatField.repeatDependencyControls
    ?.filter(
      (item: any) =>
        item?.name === triggerName &&
        (item?.dependencyAction ?? "").toLowerCase() === "hide"
    )
    ?.forEach((control: any) => {
      control.dependecyFields?.forEach((depField: any) => {
        hideManagedControlIds.add(depField.controlId);
      });
    });

  section.fields.forEach((field: any) => {
    if (isRepeatField(field) || !hideManagedControlIds.has(field.controlId)) {
      return;
    }

    const inSelectedHideList =
      action === "hide" && depControlIds.has(field.controlId);
    const shouldShow = !inSelectedHideList;

    field.visible = shouldShow;
    field.displayType = shouldShow
      ? stripDNone(field.displayType)
      : ensureDNone(field.displayType);
  });
};

const repeatShowSelectedDepControls = (
  clonedArray: any,
  index: number,
  id: string,
  name: string,
  fieldIndex: number
) => {
  const repeatField = clonedArray[index]?.fields[fieldIndex];
  const selectedControl = repeatField?.repeatDependencyControls?.find(
    (item: any) => item?.optionID == id && item?.name == name
  );

  if (!selectedControl) return;

  const action = (selectedControl.dependencyAction ?? "").toLowerCase();

  if (action === "show") {
    selectedControl.dependecyFields?.forEach((depField: any) => {
      depField.displayType = stripDNone(depField.displayType);
    });
  } else if (action === "hide") {
    selectedControl.dependecyFields?.forEach((depField: any) => {
      depField.displayType = ensureDNone(depField.displayType);
    });
  }
};

export const showDepRepeatFields = (
  Inputs: any,
  index: any,
  id: any,
  name: any,
  fieldIndex?: any,
  controlId?: any
) => {
  const clonedArray = JSON.parse(JSON.stringify(Inputs));
  if (!id || !name || fieldIndex === undefined) return clonedArray;

  const repeatField = clonedArray[index]?.fields[fieldIndex];
  if (!repeatField) return clonedArray;

  repeatHideDepControls(clonedArray, index, id, name, fieldIndex);
  repeatShowSelectedDepControls(clonedArray, index, id, name, fieldIndex);

  const filterDepControls = repeatField.repeatDependencyControls?.find(
    (item: any) => item?.optionID == id && item?.name == name
  );

  if (filterDepControls) {
    const action = (filterDepControls?.dependencyAction ?? "").toLowerCase();

    repeatField.repeatFields?.forEach((controlField: any) => {
      const depfound = filterDepControls?.dependecyFields?.find(
        (depField: any) => depField?.controlId == controlField?.controlId
      );

      const shouldShow =
        (depfound && action === "show") ||
        controlField.controlId == controlId;

      controlField.visible = shouldShow;
      controlField.displayType = shouldShow
        ? stripDNone(controlField.displayType)
        : ensureDNone(controlField.displayType);
    });
  }

  hideFields(clonedArray, index, id, name, fieldIndex);
  applySectionLevelRepeatFields(clonedArray[index], fieldIndex, id, name);

  const visibleRepeatFieldIds = new Set(
    repeatField.repeatFields
      ?.filter(
        (field: any) =>
          field?.visible !== false &&
          !(field?.displayType || "").includes("d-none")
      )
      ?.map((field: any) => field.controlId) ?? []
  );

  repeatField.repeatDependencyControls?.forEach((control: any) => {
    control.dependecyFields?.forEach((depField: any) => {
      if (visibleRepeatFieldIds.has(depField.controlId)) {
        depField.displayType = ensureDNone(depField.displayType);
      }
    });
  });

  return clonedArray;
};

/** Match Radio2 / form defaultValue handling when API pads option text (e.g. "Spouse    "). */
const normalizeOptionText = (value: any) =>
  String(value ?? "")
    .split(" ")
    .join("")
    .toLowerCase();

const findOptionByValue = (field: any, value: string) => {
  const normalizedValue = normalizeOptionText(value);
  if (!normalizedValue) return undefined;

  return field?.options?.find(
    (option: any) =>
      normalizeOptionText(option.value) === normalizedValue ||
      normalizeOptionText(option.label) === normalizedValue
  );
};

const getFieldSelectedValue = (field: any) => {
  if (field?.defaultValue !== undefined && field?.defaultValue !== null && field?.defaultValue !== "") {
    return field.defaultValue;
  }
  return (
    field?.options?.find((option: any) => option.isSelectedDefault)?.value ?? ""
  );
};

const getRepeatBillingTypeField = (repeatField: any) =>
  repeatField?.repeatFields?.find(
    (field: any) => field.systemFieldName === "BillingType"
  );

const findRepeatDependencyControl = (repeatField: any, triggerField: any) => {
  if (!repeatField?.repeatDependencyControls?.length || !triggerField?.systemFieldName) {
    return null;
  }

  const selectedValue = getFieldSelectedValue(triggerField);
  if (!selectedValue) return null;

  const option = findOptionByValue(triggerField, selectedValue);
  const normalizedValue = normalizeOptionText(selectedValue);

  return (
    repeatField.repeatDependencyControls.find(
      (control: any) =>
        control.name === triggerField.systemFieldName &&
        (normalizeOptionText(control.value) === normalizedValue ||
          normalizeOptionText(control.label) === normalizedValue ||
          (option != null &&
            (control.optionID == option.id ||
              control.optionDataID == option.optionDataID ||
              control.optionID == option.optionDataID)))
    ) ?? null
  );
};

/** Billing types whose API dependency exposes no child fields (e.g. Client Bill). */
const billingTypeHasNoChildDependencies = (repeatField: any) => {
  const billingField = getRepeatBillingTypeField(repeatField);
  if (!billingField || !getFieldSelectedValue(billingField)) return false;

  const depControl = findRepeatDependencyControl(repeatField, billingField);
  if (!depControl) return false;

  return !(depControl.dependecyFields?.length > 0);
};

export const isRepeatFieldVisible = (field: any) =>
  field?.visible !== false && !(field?.displayType || "").includes("d-none");

/** Section-level fields validate separately from Repeat groups; expose Repeat values so payload `when('$Field')` rules can resolve. */
export const getRepeatFieldValues = (section: any) => {
  const values: Record<string, any> = {};
  for (const field of section?.fields ?? []) {
    if (field?.uiType !== "Repeat") continue;
    for (const repeatField of field.repeatFields ?? []) {
      const key = repeatField?.systemFieldName;
      if (!key) continue;
      values[key] = repeatField.defaultValue;
    }
  }
  return values;
};

/** File controls store "" / [] when empty; yup.mixed().required() only fails for null/undefined. */
export const getYupValueForField = (field: any, sectionFields?: any[]) => {
  const value = getFieldValueForValidation(field, sectionFields);
  if (field?.uiType === "File" || field?.uiType === "ChooseFile") {
    if (value == null || value === "") return undefined;
    if (Array.isArray(value) && value.length === 0) return undefined;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed || trimmed === "[]" || trimmed === "null") return undefined;
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && parsed.length === 0) return undefined;
      } catch {
        // keep the original string
      }
    }
  }
  return value;
};

export const applyRepeatFieldDependencies = (
  inputs: any,
  sectionIndex: number,
  repeatFieldIndex: number
) => {
  let result = inputs;
  let repeatField = result?.[sectionIndex]?.fields?.[repeatFieldIndex];
  if (!repeatField?.repeatDependencyControls?.length) return result;

  const sortedFieldIds = [...(repeatField.repeatFields ?? [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((field: any) => field.controlId);

  for (const controlId of sortedFieldIds) {
    // Re-read from the latest clone — showDepRepeatFields replaces the tree each pass.
    const field = repeatField.repeatFields?.find(
      (f: any) => f.controlId === controlId
    );
    if (!field) continue;

    // Only apply dependencies for fields the API dependency chain has exposed.
    // Patient data may populate hidden triggers (e.g. RelationshipToInsured) but
    // those must not show child fields unless a visible parent selected them.
    if (!isRepeatFieldVisible(field)) continue;

    const depControl = findRepeatDependencyControl(repeatField, field);
    if (!depControl?.optionID) continue;

    // Prefer the option's clean value over API-padded text (e.g. "Spouse    ").
    const matchedOption = findOptionByValue(field, getFieldSelectedValue(field));
    if (matchedOption) {
      field.defaultValue = matchedOption.value;
    }

    result = showDepRepeatFields(
      result,
      sectionIndex,
      depControl.optionID,
      depControl.name,
      repeatFieldIndex,
      field.controlId
    );
    repeatField = result[sectionIndex].fields[repeatFieldIndex];
  }

  return result;
};

export const applyRepeatDependenciesOnLoad = (inputs: any) => {
  let result = inputs;
  result?.forEach((section: any, sectionIndex: number) => {
    section?.fields?.forEach((field: any, fieldIndex: number) => {
      if (isRepeatField(field)) {
        result = applyRepeatFieldDependencies(result, sectionIndex, fieldIndex);
      }
    });
  });
  return result;
};

const isRepeatEndField = (field: any) =>
  field?.uiType === "RepeatEnd" ||
  field?.systemFieldName === "RepeatEnd" ||
  field?.uiTypeId === 26;

const dependencyHidesRepeatAdd = (repeatBlock: any): boolean => {
  const sortedFields = [...(repeatBlock.repeatFields ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );

  for (const triggerField of sortedFields) {
    if (!isRepeatFieldVisible(triggerField)) continue;

    const depControl = findRepeatDependencyControl(repeatBlock, triggerField);
    if (!depControl) continue;

    const action = (depControl.dependencyAction ?? "").toLowerCase();
    if (action !== "hide" || !depControl.dependecyFields?.length) continue;

    const hidesRepeatEnd = depControl.dependecyFields.some((dep: any) =>
      isRepeatEndField(dep)
    );
    const hidesRepeatChild = depControl.dependecyFields.some((dep: any) =>
      repeatBlock.repeatFields?.some(
        (repeatChild: any) =>
          repeatChild.controlId === dep.controlId &&
          repeatChild.controlId !== triggerField.controlId
      )
    );

    if (hidesRepeatEnd || hidesRepeatChild) {
      return true;
    }
  }

  return false;
};

export const getRepeatUIStateFromInputs = (inputs: any) => {
  let showButton = true;

  inputs?.forEach((section: any) => {
    section?.fields?.forEach((repeatBlock: any) => {
      if (!isRepeatField(repeatBlock)) return;

      if (billingTypeHasNoChildDependencies(repeatBlock)) {
        showButton = false;
        return;
      }

      const repeatEnd = repeatBlock.repeatFields?.find((repeatChild: any) =>
        isRepeatEndField(repeatChild)
      );

      if (
        (repeatEnd && !isRepeatFieldVisible(repeatEnd)) ||
        dependencyHidesRepeatAdd(repeatBlock)
      ) {
        showButton = false;
      }
    });
  });

  return {
    showButton,
    checkbox: !showButton,
    disableCheckbox: !showButton,
    ins: !showButton,
  };
};

/////hide deps
const hideFields = (
  clonedArray: any,
  index: number,
  id: string,
  name: string,
  fieldIndex?: any
) => {
  let controlDataIDArr: any = [];
  let filterDepControlsHide = clonedArray[index]?.fields[
    fieldIndex
  ]?.repeatDependencyControls.filter(
    (item: any) => item?.optionID != id && item?.name == name
    // ||      (item?.optionID != id && item?.name != name)
  );

  filterDepControlsHide?.forEach(
    (element: any, depindex: number, arrayItself: any) => {
      arrayItself[depindex].dependecyFields.forEach(
        (element: any, DepFieldindex: number, arrayItselDepFields: any) => {
          if (
            arrayItselDepFields[DepFieldindex]?.displayType.includes("d-none")
          ) {
            // return;
          } else {
            arrayItselDepFields[DepFieldindex].displayType =
              arrayItselDepFields[DepFieldindex].displayType + " " + "d-none";
          }

          //    var selectedValue = arrayItselDepFields[DepFieldindex].defaultValue;
          arrayItselDepFields[DepFieldindex]?.options?.forEach(
            (element: any, Optindex: number, arrayItself: any) => {
              //  var optionValue =
              //    arrayItselDepFields[DepFieldindex]?.options[Optindex]?.value;
              // if (optionValue == selectedValue) {
              var optionId =
                arrayItselDepFields[DepFieldindex]?.options[Optindex]?.id;
              var optionName =
                arrayItselDepFields[DepFieldindex]?.options[Optindex]?.name;
              hideFields(clonedArray, index, optionId, optionName);
              // }
            }
          );
        }
      );
    }
  );
  ///////////with depaction hide
  const getDepFields = clonedArray[index].fields[fieldIndex];
  const getDepControls =
    clonedArray[index].fields[fieldIndex]?.repeatDependencyControls;

  let filterDepControlsWithHideDepAction = getDepControls?.filter(
    (item: any) =>
      item?.optionID == id &&
      item?.name == name &&
      item?.dependencyAction == "hide"
  );

  //
  filterDepControlsWithHideDepAction?.forEach(
    (elementZ: any, i: number, arrayItself: any) => {
      arrayItself[i]?.dependecyFields?.forEach(
        (element: any, i: number, arrayItselDepFields: any) => {
          controlDataIDArr.push(element?.controlDataID);
        }
      );
      getDepFields?.repeatFields?.forEach((item: any) => {
        if (controlDataIDArr.includes(item?.controlDataID)) {
          item.displayType = item.displayType + " " + "d-none";
        }
      });
    }
  );

  ///////////with depaction hide
};
function findCollectorIdIndex(
  arr: any[],
  sectionId: number,
  systemFieldName: string
) {
  const section = arr.find((section) => section.sectionId === sectionId);
  if (section) {
    return section.fields.findIndex(
      (field: any) => field.systemFieldName === systemFieldName
    );
  }
  return -1;
}
function findSectionIndex(arr: any[], sectionId: number) {
  return arr.findIndex((section) => section.sectionId === sectionId);
}
const emptyOptions = {
  value: "0",
  label: "N/A",
};
export const collectorNameOptionsAdd = (
  Inputs: any,
  selectedFacility: string,
  collectorList: any
) => {
  let inputsCopy = [...Inputs];
  if (collectorList.length === 0) {
    inputsCopy[findSectionIndex(inputsCopy, 4)].fields[
      findCollectorIdIndex(inputsCopy, 4, "CollectorID")
    ].defaultValue = 0;
    inputsCopy[findSectionIndex(inputsCopy, 4)].fields[
      findCollectorIdIndex(inputsCopy, 4, "CollectorID")
    ].options = emptyOptions;
  }
  if (collectorList.length > 0) {
    inputsCopy[findSectionIndex(inputsCopy, 4)].fields[
      findCollectorIdIndex(inputsCopy, 4, "CollectorID")
    ].options = collectorList;
  }
  return inputsCopy;
};

export const FacilityAdd = (Inputs: any, facilityList: any) => {
  let inputsCopy = [...Inputs];

  inputsCopy[findSectionIndex(inputsCopy, 2)].fields[
    findCollectorIdIndex(inputsCopy, 2, "FacilityID")
  ].options = facilityList;
  return inputsCopy;
};

export const loadRequsitionFormInputs = async (requsitionMenuId: string) => {
  try {
    let response = await UserManagementService?.GetSystemFields(
      requsitionMenuId
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const arrayStrTurnOver = (
  defaultValue: string,
  currentInputValue: string
) => {
  let splittedArr = defaultValue.split(",");
  let filteredSplittedArr = splittedArr.filter(
    (splittedArrayData: any) => splittedArrayData !== currentInputValue
  );
  let filteredSplittedArrToCommaSeperatedStr = filteredSplittedArr.toString();
  return filteredSplittedArrToCommaSeperatedStr;
};

export const commaSeperatedStrSlice = (requsitionInfo: any) => {
  let splittedArr = requsitionInfo.split(",");
  let getSplittedArrObj = {
    requsitionId: splittedArr[0],
    requisitionName: splittedArr[1],
  };
  return getSplittedArrObj;
};
export const destructCommaSeperatedStrSlice = (str: any) => {
  if (!str.includes(",")) return str;
  let extractionFromObject = commaSeperatedStrSlice(str);
  let extractedValueFromObject = extractionFromObject.requisitionName;
  return extractedValueFromObject;
};
export const panelsArrMakerToSend = (
  index: number,
  panels: any,
  panelsArrToAppendCopy: any,
  checked: boolean
) => {
  let panelsDataToAppend = panels;
  panelsDataToAppend?.testOptions?.forEach((panelsDataToAppendInfo: any) => {
    panelsDataToAppendInfo.isSelected = checked;
  });
  panelsArrToAppendCopy = [...panelsArrToAppendCopy, panelsDataToAppend];
  return panelsArrToAppendCopy;
};
export const panelsArrMakerToSend2 = (
  index: number,
  panels: any,
  checked: boolean
) => {
  let panelsArrToAppendCopy: any = [];
  let panelsDataToAppend = panels;
  panelsDataToAppend?.testOptions?.forEach((panelsDataToAppendInfo: any) => {
    panelsDataToAppendInfo.isSelected = checked;
  });
  panelsArrToAppendCopy = [...panelsArrToAppendCopy, panelsDataToAppend];
  return panelsArrToAppendCopy;
};
export const panelsArrItemRemoval = (
  panelID: number,
  panelsArrToAppendCopy: any
) => {
  let panelsArrToAppendCopyWithRemovedItem = panelsArrToAppendCopy.filter(
    (panelsArrToAppendCopyInfo: any) =>
      panelsArrToAppendCopyInfo.panelID !== panelID
  );
  return panelsArrToAppendCopyWithRemovedItem;
};

export const panelsArrItemAddChild = (
  index: any,
  parentPanelName: any,
  checkedTestOptions: any,
  panelsArrToAppendCopy: any,
  panelsCopy: any
) => {
  let selectedParentPanelIndex;
  if (panelsArrToAppendCopy.length > 0) {
    selectedParentPanelIndex = panelsArrToAppendCopy.findIndex(
      (panelsArrToAppendCopyData: any) =>
        panelsArrToAppendCopyData.panelName === parentPanelName
    );
  }
  let selectedPanel = panelsCopy[index];
  let selectedPanelCopy = JSON.parse(JSON.stringify(selectedPanel));
  let selectedTestoptions = selectedPanel.testOptions.filter(
    (selectedTestOpt: any) => selectedTestOpt?.isSelected
  );
  selectedPanelCopy.testOptions = selectedTestoptions;
  if (selectedParentPanelIndex > 0 || selectedParentPanelIndex == 0) {
    panelsArrToAppendCopy[selectedParentPanelIndex] = selectedPanelCopy;
  }
  if (selectedParentPanelIndex < 0) {
    panelsArrToAppendCopy = [...panelsArrToAppendCopy, selectedPanelCopy];
  }
  if (selectedParentPanelIndex === undefined) {
    panelsArrToAppendCopy = [...panelsArrToAppendCopy, selectedPanelCopy];
  }

  return panelsArrToAppendCopy;
};
export const panelsArrItemRemovalChild = (
  index: number,
  testID: string,
  panelsArrToAppendCopy: any
) => {
  let testOptionsWithRemovedItem = panelsArrToAppendCopy[
    index
  ]?.testOptions?.filter(
    (testOptionsInfo: any) => testOptionsInfo.testID !== testID
  );

  let check = panelsArrToAppendCopy[index];
  if (check) {
    panelsArrToAppendCopy[index].isSelected = false;
    panelsArrToAppendCopy[index].testOptions = testOptionsWithRemovedItem;
  }

  return panelsArrToAppendCopy;
};

export const typeCheckForFieldValue = (fieldValue: any) => {
  let objectKeysLength = Object.keys(fieldValue).length;
  if (objectKeysLength === 2) {
    return <span className="d-flex">{fieldValue?.label}</span>;
  }
  if (objectKeysLength === 4 && Array.isArray(fieldValue?.testOptions)) {
    return (
      <div className="d-flex">
        <h6>{fieldValue?.panelName}</h6>
        {Array.isArray(fieldValue?.testOptions) &&
          fieldValue?.testOptions.map((testOptions: any) => (
            <span>{testOptions?.testName}</span>
          ))}
      </div>
    );
  } else if (
    objectKeysLength === 3 &&
    !Array.isArray(fieldValue?.testOptions)
  ) {
    return (
      <div className="d-flex">
        <span>{fieldValue?.Code}</span>:<span>{fieldValue?.Description}</span>
      </div>
    );
  } else return "";
};

export const getDataForRequsitionLoad = (inputsResponseCopy: any) => {
  let obj: any = {
    facilityId: "",
    insuranceTypeId: "",
    insuranceDataId: ""
  };
  const facilitySection = GetSingleElementFromArray(
    inputsResponseCopy,
    (item: any) => {
      return (
        item.sectionName == "Facility Test" || item.sectionName == "Facility"
      );
    }
  );
  const faclityControl = GetSingleElementFromArray(
    facilitySection.fields,
    (item: any) => {
      return item.systemFieldName == "FacilityID";
    }
  );
  obj.facilityId = parseInt(faclityControl?.defaultValue) || 0;

  const billingSection = GetSingleElementFromArray(
    inputsResponseCopy,
    (item: any) => {
      return (
        item.sectionName == "Billing Information" ||
        item.sectionName == "Billing"
      );
    }
  );
  const repeatFieldControl = GetSingleElementFromArray(
    billingSection.fields,
    (item: any) => {
      return item.uiType == "Repeat";
    }
  );
  const billingTypeControl = GetSingleElementFromArray(
    repeatFieldControl.repeatFields,
    (item: any) => {
      return (
        item.systemFieldName == "BillingType" ||
        item.systemFieldName == "billingType"
      );
    }
  );
  const SellectedBillingOption = GetSingleElementFromArray(
    billingTypeControl.options,
    (item: any) => {
      return item.isSelectedDefault == true;
    }
  );

  obj.insuranceTypeId = parseInt(SellectedBillingOption?.id) || 0;
  obj.insuranceDataId = parseInt(SellectedBillingOption?.optionDataID) || 0

  // let filterInputsArrToGetFacilitySection = inputsResponseCopy.filter(
  //   (InputsData: any) => InputsData.sectionName === "Facility"
  // );
  // let filterInputsArrToGetBillingInformationSection = inputsResponseCopy.filter(
  //   (InputsData: any) => InputsData.sectionName === "Billing Information"
  // );
  // obj.facilityId = parseInt(
  //   filterInputsArrToGetFacilitySection[0].fields[0].defaultValue
  // );
  // let insurantypeId =
  //   filterInputsArrToGetBillingInformationSection[0]?.fields[0]?.repeatFields[0]?.options?.filter(
  //     (optionsInfo: any) => optionsInfo.isSelectedDefault
  //   );
  // let integerInsuranceId = parseInt(insurantypeId[0].id);
  // obj.insuranceTypeId = integerInsuranceId;
  return obj;
};
export const extractDropDownSelectedValue = (
  fields: any,
  fieldToFilter: string
) => {
  let facilityFilteredDataForEdit = fields?.find(
    (facilityInfo: any) => facilityInfo?.systemFieldName == fieldToFilter
  );

  let id = facilityFilteredDataForEdit?.defaultValue;
  let selectedFacilityInfoArray = facilityFilteredDataForEdit?.options?.find(
    (faciltyInfo: any) => faciltyInfo?.value == id
  );

  return selectedFacilityInfoArray;
};

// export const setDropDownValue = (
//   options: any,
//   selectedValue: any,
//   uiType?: string
// ) => {
//   if (uiType === "ServerSideDynamicDropDown" && selectedValue) {
//     // handleServerSideDropdownOnChange()
//   }
//   let obj = { value: "0", label: "N/A" };

//   if (selectedValue === 0 || !selectedValue) {
//     return obj;
//   } else {
//     if (!options) return;
//     let selectedDropDownValue = options?.find(
//       (optionsInfo: any) => optionsInfo?.value == selectedValue
//     );
//     return selectedDropDownValue;
//   }
// };
export const setDropDownValue = (
  options: any[],
  selectedValue: any,
  reqId: any
) => {
  if (!selectedValue && !reqId) {
    return [];
  }
  if (!Array.isArray(options) && !reqId) {
    return [];
  }
  if (!Array.isArray(options) && reqId) {
    return [{ value: "N/A", label: "N/A" }];
  }
  const selectedDropDownValue = options.find(
    (option) => option?.value === selectedValue
  );
  return selectedDropDownValue || { value: "N/A", label: "N/A" };
};

export const getLengthFromArray = (array: any[], condition: any) => {
  return array.filter(condition).length;
};
export const getItemFromArray = (array: any[], condition: any) => {
  return array.find(condition);
};
export const getItemsFromArray = (array: any[], condition: any) => {
  return array.filter(condition);
};
export const camelize = (str: string) => {
  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match: any, index: any) => {
    if (+match === 0) return ""; // or if (/\s+/.test(match)) for white spaces
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
};
const lowerize = (obj: any) =>
  Object.keys(obj).reduce((acc: any, k: any) => {
    acc[k.toLowerCase()] = obj[k];
    return acc;
  }, {});

// export const setPatientValues = async (
//   patientData: any,
//   Inputs: any,
//   setInputs: any,
//   loadReqSec: any,
//   setDisableCheckbox?: any
// ) => {
//   localStorage.setItem("insurnceID", patientData.Insurances[0]?.insuranceID);
//   if (patientData.Insurances.length > 1) {
//     setDisableCheckbox(true);
//   } else {
//     setDisableCheckbox(false);
//   }
//   console.log(patientData, "patientData");
//   // Lowercase all keys in patientData
//   patientData = lowerize(patientData);
//   // Deep copy of the Inputs array
//   let inputsCopy = JSON.parse(JSON.stringify(Inputs));
//   // Find the index for "Patient" section
//   let patientIndex = inputsCopy.findIndex(
//     (item: any) => item?.sectionName === "Patient"
//   );
//   // Set default values for the "Patient" section fields
//   inputsCopy[patientIndex].fields.forEach((fieldsData: any) => {
//     fieldsData.defaultValue =
//       patientData[fieldsData.systemFieldName.toLowerCase()];
//   });
//   // Find the index for "Billing Information" section
//   let billingIndex = inputsCopy.findIndex(
//     (item: any) => item?.sectionName === "Billing Information"
//   );

//   // Insurance data and repeat field control checks
//   let insuranceDataLength = patientData.insurances.length;
//   let RepeatFieldsLength = getLengthFromArray(
//     inputsCopy[billingIndex].fields,
//     (item: any) => item.uiTypeId === 33
//   );
//   let repeatstartControl = getItemFromArray(
//     inputsCopy[billingIndex].fields,
//     (item: any) => item.uiTypeId === 33
//   );
//   let allRepeatStartControls = getItemsFromArray(
//     inputsCopy[billingIndex].fields,
//     (item: any) => item.uiTypeId === 33
//   );
//   let allControlsToRemove = allRepeatStartControls.length;
//   // Ensure enough repeat start controls for all insurances
//   while (allRepeatStartControls.length < insuranceDataLength) {
//     allRepeatStartControls.push(JSON.parse(JSON.stringify(repeatstartControl)));
//   }
//   // Set default values for each insurance
//   for (let insIndex = 0; insIndex < insuranceDataLength; insIndex++) {
//     const insElement = lowerize(patientData.insurances[insIndex]);
//     allRepeatStartControls[insIndex].repeatFields.forEach((fieldData: any) => {
//       fieldData.defaultValue =
//         insElement[camelize(fieldData.systemFieldName.toLowerCase())];
//       let depFieldsOption = getItemFromArray(
//         allRepeatStartControls[insIndex].repeatDependencyControls,
//         (item: any) =>
//           item?.value?.toString().toLowerCase() ==
//           fieldData?.defaultValue?.toString().toLowerCase()
//       );
//       if (depFieldsOption) {
//         var action = depFieldsOption.dependencyAction;
//         // localStorage.setItem("insurnceID", depFieldsOption.optionID);
//         allRepeatStartControls[insIndex].repeatFields.forEach(
//           (fieldData: any) => {
//             var depfound = getItemFromArray(
//               depFieldsOption.dependecyFields,
//               (Item: any) => Item.controlId == fieldData.controlId
//             );
//             if (depfound && (action ?? "")?.toLowerCase() === "show") {
//               fieldData.visible = true;
//             } else {
//               fieldData.visible = false;
//             }
//             if (fieldData.controlId === 44) {
//               fieldData.visible = true;
//             }
//             if (fieldData.controlId === 45 && fieldData.defaultValue) {
//               fieldData.visible = true;
//             }
//           }
//         );
//       }
//     });
//   }
//   // Separate repeat and non-repeat fields, then combine them back
//   let AllControlsWithoutRepeat = getItemsFromArray(
//     inputsCopy[billingIndex].fields,
//     (item: any) => item.uiTypeId !== 33
//   );

//   inputsCopy[billingIndex].fields = [];
//   allRepeatStartControls.forEach((item: any) =>
//     inputsCopy[billingIndex].fields.push(item)
//   );
//   AllControlsWithoutRepeat.forEach((item: any) =>
//     inputsCopy[billingIndex].fields.push(item)
//   );
//   // Update state and then load required sections
//   setInputs(inputsCopy);
//   // Use a slight delay to ensure setInputs is processed before calling loadReqSec
//   setTimeout(() => {
//     loadReqSec();
//   }, 0);
// };
const getInsuranceType = (inputs: any, id: any): any => {
  // Loop through inputs
  for (const item of inputs) {
    // Check if sectionId is 5
    if (item.sectionId === 5) {
      // Loop through fields
      for (const field of item.fields) {
        // Check if the field's displayFieldName is "Repeat Start"
        if (field.displayFieldName === "Repeat Start") {
          // Loop through repeatFields
          for (const i of field.repeatFields) {
            // Check if systemFieldName is "BillingType"
            if (i.systemFieldName === "BillingType") {
              // Loop through options
              for (const j of i.options) {
                // Check if optionDataID matches the given id

                if (j.optionDataID == id) {
                  return j;
                }
              }
            }
          }
        }
      }
    }
  }

  // Return null if no match is found
  return null;
};
export const setPatientValues = async (
  patientData: any,
  Inputs: any,
  setInputs: any,
  loadReqSec: any,
  setDisableCheckbox?: any,
  setCheckbox?: any,
  setIns?: any,
  setShowButton?: any,
  setDisableSSN?: any
) => {


  // Lowercase all keys in patientData
  patientData = lowerize(patientData);

  // Deep copy of the Inputs array
  let inputsCopy = JSON.parse(JSON.stringify(Inputs));

  // Set Patient section values
  let patientIndex = inputsCopy.findIndex((item: any) => item?.sectionId === 3);
  inputsCopy[patientIndex].fields.forEach((fieldsData: any) => {
    fieldsData.defaultValue =
      patientData[fieldsData.systemFieldName.toLowerCase()];
  });


  const hasInsurance =
    Array.isArray(patientData.insurances) &&
    patientData.insurances.some((ins: any) =>
      ins && Object.values(ins).some(val => val !== null && val !== "")
    );

  if (hasInsurance) {

    localStorage.setItem(
      "insuranceOptionId",
      patientData.insurances[0]?.insuranceID
    );
    localStorage.setItem("patientID", patientData.patientid);

    // Handle insurance fields logic (same logic as before)
    let billingIndex = inputsCopy.findIndex((item: any) => item?.sectionId === 5);
    let insuranceDataLength = patientData.insurances.length;
    let repeatstartControl = getItemFromArray(
      inputsCopy[billingIndex].fields,
      (item: any) => item.uiTypeId === 33
    );
    let allRepeatStartControls = getItemsFromArray(
      inputsCopy[billingIndex].fields,
      (item: any) => item.uiTypeId === 33
    );

    resetRepeatFieldFromState(repeatstartControl);
    allRepeatStartControls.forEach((control: any) =>
      resetRepeatFieldFromState(control)
    );

    inputsCopy[billingIndex].fields = inputsCopy[billingIndex].fields.filter(
      (item: any) => item.uiTypeId !== 33
    );

    while (allRepeatStartControls.length < insuranceDataLength) {
      allRepeatStartControls.push(
        JSON.parse(JSON.stringify(repeatstartControl))
      );
    }

    if (allRepeatStartControls.length > insuranceDataLength) {
      allRepeatStartControls = allRepeatStartControls.slice(0, insuranceDataLength);
    }

    for (let insIndex = 0; insIndex < insuranceDataLength; insIndex++) {
      const insElement = lowerize(patientData.insurances[insIndex]);

      // First pass: Set all defaultValues from insurance data
      allRepeatStartControls[insIndex].repeatFields.forEach((fieldData: any) => {
        const rawValue = insElement[camelize(fieldData.systemFieldName.toLowerCase())];

        // Match option values case-insensitively and ignore padded spaces from API.
        if (fieldData.options?.length) {
          const matchingOption = findOptionByValue(fieldData, rawValue);

          if (matchingOption) {
            fieldData.defaultValue = matchingOption.value;
            fieldData.options.forEach((option: any) => {
              option.isSelectedDefault = option.id === matchingOption.id;
            });
          } else {
            fieldData.defaultValue = rawValue;
          }
        } else {
          fieldData.defaultValue = rawValue;
        }
      });
    }

    let AllControlsWithoutRepeat = getItemsFromArray(
      inputsCopy[billingIndex].fields,
      (item: any) => item.uiTypeId !== 33
    );

    inputsCopy[billingIndex].fields = [];
    allRepeatStartControls.forEach((item: any) =>
      inputsCopy[billingIndex].fields.push(item)
    );
    AllControlsWithoutRepeat.forEach((item: any) =>
      inputsCopy[billingIndex].fields.push(item)
    );

    inputsCopy[billingIndex].fields.forEach((item: any, index: number) => {
      if (index !== 0 && item.displayFieldName === "Repeat Start") {
        item.repeatFields.forEach((rep: any) => {
          if (rep.systemFieldName === "BillingType") {
            rep.options = rep.options.filter(
              (option: any) =>
                option.value !== "Client Bill" && option.value !== "Self Pay"
            );
          }
        });
      }
    });

    inputsCopy = applyRepeatDependenciesOnLoad(inputsCopy);

    const billingRepeat = inputsCopy[billingIndex]?.fields?.find((field: any) =>
      isRepeatField(field)
    );
    const billingTypeField = billingRepeat?.repeatFields?.find(
      (field: any) => field.systemFieldName === "BillingType"
    );
    const billingOption = findOptionByValue(
      billingTypeField,
      getFieldSelectedValue(billingTypeField)
    );
    if (billingOption?.id) {
      localStorage.setItem("insurnceID", String(billingOption.id));
    } else {
      const obj = getInsuranceType(Inputs, patientData.insurances[0]?.insuranceID);
      localStorage.setItem("insurnceID", obj?.id);
    }

    const repeatUI = getRepeatUIStateFromInputs(inputsCopy);
    if (!repeatUI.showButton) {
      inputsCopy.forEach((section: any) => {
        const repeatFields = section.fields?.filter((field: any) =>
          isRepeatField(field)
        );
        if (repeatFields?.length > 1) {
          const nonRepeatFields =
            section.fields?.filter((field: any) => !isRepeatField(field)) ?? [];
          section.fields = [repeatFields[0], ...nonRepeatFields];
        }
      });
    }
    setIns?.(repeatUI.ins);
    setDisableCheckbox?.(repeatUI.disableCheckbox);
    setShowButton?.(repeatUI.showButton);
    setCheckbox?.(repeatUI.checkbox);
  }

  // Update state
  setInputs(inputsCopy);

  // Ensure inputs are set before loading required sections
  setTimeout(() => {
    loadReqSec();
  }, 0);
};

export const isJson = (str: any): boolean => {
  if (typeof str !== "string") return false;
  try {
    const parsed = JSON.parse(str);
    return typeof parsed === "object" && parsed !== null;
  } catch {
    return false;
  }
};

export const Masking = (inputValue: any, inputType: any) => {
  if (inputType !== "TextArea") return;
  let val = inputValue.replace(/\D/g, ""); // Remove non-numeric characters
  if (inputValue.length <= 10) {
    // Apply your custom mask here
    if (val.length >= 7) {
      val = `(${inputValue.slice(0, 3)}) ${inputValue.slice(
        3,
        6
      )}-${inputValue.slice(6)}`;
    } else if (inputValue.length >= 3) {
      val = `(${inputValue.slice(0, 3)}) ${inputValue.slice(3)}`;
    }
  }
  return val;
};

export const countEntriesWithSpecificText = (dataArray: any) => {
  if (!Array.isArray(dataArray)) return 0;
  return dataArray.reduce((count: any, item: any) => {
    const nonEmptyFieldsCount = Object.values(item ?? {}).reduce(
      (fieldCount: any, value: any) => {
        return String(value ?? "").trim() !== "" ? fieldCount + 1 : fieldCount;
      },
      0
    );
    return count + nonEmptyFieldsCount;
  }, 0);
};

// Panel-selection rules live in their own service-free module so they can be
// unit tested; re-exported here so existing import paths keep working.
export {
  PANEL_SELECTION_UI_TYPES,
  NON_TEST_PANEL_FIELDS,
  isPanelSelected,
  getFieldValueForValidation,
  isRequiredTestSelectionField,
  sectionsHaveTestSelection,
  getRequisitionsMissingTestSelection,
} from "./panelSelection";

const isActiveDependencyOption = (
  controllingFieldValue: any,
  dependencyControl: any
): boolean => {
  if (
    controllingFieldValue === undefined ||
    controllingFieldValue === null ||
    String(controllingFieldValue) === ""
  ) {
    return true;
  }
  return (
    String(controllingFieldValue) === String(dependencyControl?.value) ||
    String(controllingFieldValue) === String(dependencyControl?.label)
  );
};

const assignIfEmpty = (
  target: Record<string, any>,
  key: string | undefined,
  value: any
) => {
  if (!key) return;
  const existing = target[key];
  if (existing !== undefined && existing !== null && existing !== "") return;
  target[key] = value;
};

/** Values from other sections so yup `.when()` / `this.parent` can compare across sections. */
export const collectCrossSectionFieldValues = (
  sections: any[],
  currentSectionIndex: number
): Record<string, any> => {
  const values: Record<string, any> = {};
  if (!Array.isArray(sections)) return values;

  sections.forEach((section, index) => {
    if (index === currentSectionIndex) return;
    if (!section || section.isSelected === false) return;

    const sectionValues: Record<string, any> = {};
    (section.fields || []).forEach((field: any) => {
      if (!field || field.uiType === "Repeat") return;
      assignIfEmpty(
        sectionValues,
        field.systemFieldName,
        getFieldValueForValidation(field, section.fields)
      );
    });

    (section.dependencyControls || []).forEach((dependencyControl: any) => {
      if (
        !isActiveDependencyOption(
          sectionValues[dependencyControl?.name],
          dependencyControl
        )
      ) {
        return;
      }
      (dependencyControl.dependecyFields || []).forEach((depField: any) => {
        assignIfEmpty(
          sectionValues,
          depField?.systemFieldName,
          depField?.defaultValue
        );
      });
    });

    Object.keys(sectionValues).forEach((key) => {
      assignIfEmpty(values, key, sectionValues[key]);
    });
  });

  return values;
};

export const countKeysWithValues = async (obj: any) => {
  let count = 0;
  for (const key in obj) {
    if (
      obj.hasOwnProperty(key) &&
      obj[key] !== null &&
      obj[key] !== undefined &&
      obj[key] !== ""
    ) {
      count++;
    }
  }
  return count;
};
export const UpdatedArrayForPreView = async (preView: any) => {
  return preView;
};

export const ValidateRepeatInput = async (
  field: any,
  sectionindex: number,
  fieldsIndex: number,
  errorsObj: any,
  data: any
) => {
  let valueObj: any = {};
  let schemaObj: any = {};

  for (let y = 0; y < field.repeatFields.length; y++) {
    const repeatFieldItem = field.repeatFields[y];
    if (!isRepeatFieldVisible(repeatFieldItem)) continue;

    if (valueObj.hasOwnProperty(repeatFieldItem.systemFieldName)) {
      if (
        valueObj[repeatFieldItem.systemFieldName] !== null ||
        valueObj[repeatFieldItem.systemFieldName] != ""
      )
        continue;
    }
    valueObj[repeatFieldItem.systemFieldName] = repeatFieldItem.defaultValue;
    if (
      repeatFieldItem.validationExpression !== "" &&
      repeatFieldItem.validationExpression !== null
    )
      schemaObj[repeatFieldItem.systemFieldName] =
        await convertStringToYupSchema(repeatFieldItem.validationExpression);
  }
  ////for dependencies
  for (let y = 0; y < field.repeatDependencyControls.length; y++) {
    for (
      let z = 0;
      z < field.repeatDependencyControls[y].dependecyFields.length;
      z++
    ) {
      const depField = field.repeatDependencyControls[y].dependecyFields[z];
      if (!isRepeatFieldVisible(depField)) continue;

      if (valueObj.hasOwnProperty(depField.systemFieldName)) {
        if (
          valueObj[depField.systemFieldName] !== null ||
          valueObj[depField.systemFieldName] != ""
        )
          continue;
      }

      valueObj[depField.systemFieldName] = depField.defaultValue;

      if (
        depField.validationExpression !== "" &&
        depField.validationExpression !== null
      )
        schemaObj[depField.systemFieldName] = await convertStringToYupSchema(
          depField.validationExpression
        );
    }
  }
  yup.object(schemaObj).describe();
  try {
    await yup
      .object()
      .shape(schemaObj)
      .validateSync(valueObj, { abortEarly: false });
    return await fieldsRepeatLoop(
      undefined,
      sectionindex,
      fieldsIndex,
      true,
      data,
      errorsObj
    );
  } catch (e: any) {
    return await fieldsRepeatLoop(
      e,
      sectionindex,
      fieldsIndex,
      false,
      data,
      errorsObj
    );
  }
};

const fieldsRepeatLoop = async (
  errors: any,
  i: number,
  fieldsIndex: number,
  isValid: boolean,
  data: any,
  errorsObj: any = {}
) => {
  return await new Promise((resolve, reject) => {
    const looprepeateField = async () => {
      await data[i].fields[fieldsIndex].repeatFields.forEach(
        (x: any, index: number) => {
          if (!isRepeatFieldVisible(x)) {
            data[i].fields[fieldsIndex].repeatFields[index].enableRule = "";
            errorsObj[x.systemFieldName] = "";
            return;
          }
          if (!isValid) {
            let innerError = errors.inner.find((y: any) => {
              return y.path == x.systemFieldName;
            });
            //
            if (innerError) {
              //isValid = false;
              data[i].fields[fieldsIndex].repeatFields[index].enableRule =
                innerError.message;
              // errorsObj = {...errorsObj,x.systemFieldName:innerError.message}
              errorsObj[x.systemFieldName] = innerError.message;
              // setErrosTrack((preVal) => {
              //   return {
              //     ...preVal,
              //     [x.systemFieldName]: innerError.message,
              //   };
              // });
              // x.enableRule = innerError.message;
            } else {
              data[i].fields[fieldsIndex].repeatFields[index].enableRule = "";
              errorsObj[x.systemFieldName] = "";
              // setErrosTrack((preVal) => {
              //   return {
              //     ...preVal,
              //     [x.systemFieldName]: "",
              //   };
              // });
            }
          } else {
            data[i].fields[fieldsIndex].repeatFields[index].enableRule = "";
            errorsObj[x.systemFieldName] = "";
          }
        }
      );
      ///////////////for dependecies validation
      for (
        let y = 0;
        y < data[i].fields[fieldsIndex].repeatDependencyControls.length;
        y++
      ) {
        for (
          let z = 0;
          z <
          data[i].fields[fieldsIndex].repeatDependencyControls[y]
            .dependecyFields.length;
          z++
        ) {
          const depField =
            data[i].fields[fieldsIndex].repeatDependencyControls[y]
              .dependecyFields[z];
          if (!isRepeatFieldVisible(depField)) {
            depField.enableRule = "";
            errorsObj[depField.systemFieldName] = "";
            continue;
          }
          if (!isValid) {
            let innerError = errors.inner.find((er: any) => {
              return er.path == depField.systemFieldName;
            });
            //
            if (innerError) {
              //isValid = false;
              depField.enableRule = innerError.message;
              // x.enableRule = innerError.message;
              errorsObj[
                data[i].fields[fieldsIndex].repeatDependencyControls[
                  y
                ].dependecyFields[z].systemFieldName
              ] = innerError.message;
            } else {
              errorsObj[
                data[i].fields[fieldsIndex].repeatDependencyControls[
                  y
                ].dependecyFields[z].systemFieldName
              ] = "";
              data[i].fields[fieldsIndex].repeatDependencyControls[
                y
              ].dependecyFields[z].enableRule = "";
            }
          } else {
            errorsObj[
              data[i].fields[fieldsIndex].repeatDependencyControls[
                y
              ].dependecyFields[z].systemFieldName
            ] = "";
            data[i].fields[fieldsIndex].repeatDependencyControls[
              y
            ].dependecyFields[z].enableRule = "";
          }
        }
      }
      return data;
    };

    return resolve(looprepeateField());
  });
};

async function convertStringToYupSchema(
  stringSchema: string | undefined
): Promise<yup.Schema<unknown, any, any, "">> {
  try {
    if (!stringSchema) {
      // Return a default Yup schema or `null` schema if the input is undefined
      return yup.mixed().nullable().optional();
    }
    const schema = await new Function("yup", `return ${stringSchema}`)(yup);
    if (schema instanceof yup.Schema) {
      return schema;
    } else {
      throw new Error("Invalid schema string.");
    }
  } catch (error: any) {
    throw new Error(
      `Error converting string to Yup schema: ${error.message} ${stringSchema}`
    );
  }
}

export const modifyValidationSchemaForSaveLater = async (
  Inputs: any,
  validatedSystemFields: any[] = []
) => {
  let errorsObj: any = {};

  for (let i = 0; i < Inputs.length; i++) {
    let valueObj: any = collectCrossSectionFieldValues(Inputs, i);
    let schemaObj: any = {};
    for (let y = 0; y < Inputs[i].fields.length; y++) {
      // for repeated field like billing Type
      if (Inputs[i].isSelected) {
        await ValidateRepeatInput(Inputs[i].fields[y], i, y, errorsObj, Inputs);
        if (
          validatedSystemFields.includes(Inputs[i].fields[y].systemFieldName)
        ) {
          valueObj[Inputs[i].fields[y].systemFieldName] =
            Inputs[i].fields[y].defaultValue;
          if (
            Inputs[i].fields[y].validationExpression !== "" &&
            Inputs[i].fields[y].validationExpression !== null
          )
            schemaObj[Inputs[i].fields[y].systemFieldName] =
              await convertStringToYupSchema(
                Inputs[i].fields[y].validationExpression
              );
        }

        try {
          await yup
            .object()
            .shape(schemaObj)
            .validateSync(valueObj, { abortEarly: false });

          await Inputs[i].fields.forEach((x: any, index: number) => {
            Inputs[i].fields[index].enableRule = "";
            errorsObj[x.systemFieldName] = "";
          });
        } catch (errors: any) {
          await Inputs[i].fields.forEach((x: any, index: number) => {
            let innerError = errors?.inner?.find((y: any) => {
              return y.path == x.systemFieldName;
            });
            //
            if (innerError) {
              Inputs[i].fields[index].enableRule = innerError.message;
              errorsObj[x.systemFieldName] = innerError.message;
            } else {
              Inputs[i].fields[index].enableRule = "";
              errorsObj[x.systemFieldName] = "";
            }
          });
        }
      }
    }
  }
  return { data: Inputs, validation: errorsObj };
};

export const moveSignaturesToEnd = (newFormDataApi: any) => {
  let result = isJson(newFormDataApi);
  if (!result) return;
  let deepCopy = JSON.parse(JSON.stringify(newFormDataApi));
  let arr: any = [];
  Array.isArray(deepCopy?.requisitions) &&
    deepCopy?.requisitions.forEach((reqData: any) => {
      reqData?.reqSections?.forEach((requisitionsData: any) => {
        if (
          requisitionsData?.sectionId === 13 ||
          requisitionsData?.sectionId === 14
        ) {
          arr.push(requisitionsData);
        }
      });
    });
  return arr;
};

export const checkBox = (
  choiceValue: string,
  defaultValue: string,
  repeatFieldIndex: number
) => {
  if (repeatFieldIndex) {
  }
};

export const setJSONDataFormat = (defaultValue: any) => {
  let result = isJson(defaultValue);
  if (result) {
    defaultValue = JSON.parse(defaultValue);
    return defaultValue;
  } else {
    return defaultValue;
  }
};
const extractFocussedInputAddressForBillingInformation = (fieldsData: any) => {
  for (let innerIndex = 0; innerIndex < fieldsData.length; innerIndex++) {
    //
    if (fieldsData[innerIndex]?.enableRule) {
      return fieldsData[innerIndex]?.systemFieldName;
    }
  }
};
export const extractFocussedInputAddress = (validatedData: any) => {
  let breakLoop = false;
  let toFocusFieldInfo: any = {};
  for (let index = 0; index < validatedData.length; index++) {
    if (breakLoop) {
      break;
    }
    for (
      let innerIndex = 0;
      innerIndex < validatedData[index].fields.length;
      innerIndex++
    ) {
      if (validatedData[index].fields[innerIndex]?.repeatFields?.length > 0) {
        let billingInfoFocussedField =
          extractFocussedInputAddressForBillingInformation(
            validatedData[index].fields[innerIndex]?.repeatFields
          );
        toFocusFieldInfo.fieldName = billingInfoFocussedField;
        if (billingInfoFocussedField) {
          breakLoop = true;
          break;
        }
      }
      if (validatedData[index].fields[innerIndex]?.enableRule) {
        toFocusFieldInfo.fieldName =
          validatedData[index].fields[innerIndex]?.systemFieldName;
        breakLoop = true;
        break;
      }
    }
  }
  return toFocusFieldInfo.fieldName;
};
// export const do_it = async (
//   obj: any,
//   callback: any,
//   Inputs: any,
//   index: any
// ) => {
//   getIpAddress().then((results) => {
//     obj.ipAddress = results;
//   });
//   getSignatureText(obj, Inputs, index).then((results) => {
//     callback(results.data);
//   });
//   return;
// };

export const do_it = async (
  obj: any,
  callback: any,
  Inputs: any,
  index: any
) => {
  try {
    const ipAddress = await getIpAddress(); // Wait for IP address
    obj.ipAddress = ipAddress; // Add IP address to the object

    const signatureData = await getSignatureText(obj, Inputs, index); // Wait for signature text
    callback(signatureData.data); // Pass the result to the callback
  } catch (error) {
    console.error(error);
  }
};

export const getIpAddress = async () => {
  let ipAddress = "";
  let response = await fetch("https://api.ipify.org?format=json")
    .then((response) => response.json())
    .then((data) => {
      ipAddress = data.ip;
      return data.ip;
    })
    .catch((error) => console.log(error));
  return await response;
};

export const getSignatureText = async (
  strObj: any,
  Inputs: any,
  index: any
) => {
  let response = RequisitionType.generateSignature(strObj)
    .then((res: any) => {
      return res;
    })
    .catch((err: any) => { })
    .finally(() => { });
  return response;
};

export const dateFormatSetter = (date: string) => {
  if (!date) return;
  let convertedDate: any = new Date(date);
  if (isNaN(convertedDate)) return "";
  if (date) {
    return convertedDate;
  } else if (!date) {
    return "";
  }
};

export const getSpecimenSourceIndex = (inputsCopy: any) => {
  let specimenSourceIndex = inputsCopy.findIndex(
    (specimenSourceInfo: any) => specimenSourceInfo.sectionId == 11
  );
  return specimenSourceIndex;
};
export const getICDPanelsIndex = (inputsCopy: any) => {
  let ICDPanelsIndex = inputsCopy.findIndex(
    (Info: any) => Info.sectionId == 12
  );
  return ICDPanelsIndex;
};

export const getComorbiditiesPanelsIndex = (inputsCopy: any) => {
  const byField = inputsCopy.findIndex((Info: any) =>
    Info?.fields?.some(
      (field: any) => field.systemFieldName === "ComorbidityPanels"
    )
  );
  if (byField !== -1) return byField;
  return inputsCopy.findIndex((Info: any) => Info.sectionId == 103);
};

// Ordering rules for the requisition Testing Option section. Panels are ordered
// by panel.sortOrder, the typeOfTest groups within a panel by subPanelSortOrder,
// and the tests within a group by testSortOrder. Anything without an order is
// pushed to the end; ties fall back to the test name so the list stays stable.
const hasOrder = (value: any) => value !== null && value !== undefined;

export const comparePanelsBySortOrder = (a: any, b: any) => {
  const aHas = hasOrder(a?.sortOrder);
  const bHas = hasOrder(b?.sortOrder);
  if (aHas && bHas) return a.sortOrder - b.sortOrder;
  if (aHas) return -1;
  if (bHas) return 1;
  return 0;
};

export const compareTestGroupsBySubPanelSortOrder = (
  [, aTests]: any,
  [, bTests]: any
) => {
  const minOrder = (tests: any[]) =>
    Math.min(
      ...tests.map((test: any) => test?.subPanelSortOrder).filter(hasOrder)
    );
  const aMin = minOrder(aTests);
  const bMin = minOrder(bTests);
  const aHas = isFinite(aMin);
  const bHas = isFinite(bMin);
  if (aHas && bHas) return aMin - bMin;
  if (aHas) return -1;
  if (bHas) return 1;
  return 0;
};

export const compareTestsBySortOrder = (a: any, b: any) => {
  const aHas = hasOrder(a?.testSortOrder);
  const bHas = hasOrder(b?.testSortOrder);
  if (aHas && bHas && a.testSortOrder !== b.testSortOrder) {
    return a.testSortOrder - b.testSortOrder;
  }
  if (aHas !== bHas) return aHas ? -1 : 1;
  return (a?.testName ?? '').localeCompare(b?.testName ?? '');
};

// For controls that render a panel's tests as one flat list instead of grouping
// them by typeOfTest. There is no group loop to carry subPanelSortOrder, so it
// becomes the first key here and testSortOrder breaks ties within a sub-panel.
export const compareTestsBySubPanelAndSortOrder = (a: any, b: any) => {
  const aHas = hasOrder(a?.subPanelSortOrder);
  const bHas = hasOrder(b?.subPanelSortOrder);
  if (aHas && bHas && a.subPanelSortOrder !== b.subPanelSortOrder) {
    return a.subPanelSortOrder - b.subPanelSortOrder;
  }
  if (aHas !== bHas) return aHas ? -1 : 1;
  return compareTestsBySortOrder(a, b);
};

const parseIcdComorbidityDefaultValue = (defaultValue: any) => {
  if (Array.isArray(defaultValue)) return defaultValue;
  if (typeof defaultValue === "string") {
    try {
      const parsed = JSON.parse(defaultValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const toComparableId = (value: any) =>
  value === undefined || value === null ? undefined : value.toString().trim();

export const hideIcdAndComorbidityForPanel = (
  inputsCopy: any,
  panelName: string,
  panelID?: string | number
) => {
  const panelIdStr = toComparableId(panelID);

  // A panel can be identified by either key depending on the caller: the tests
  // section and the ICD/comorbidity section do not always agree on panelName
  // casing, and some callers only know the panelID.
  const isTargetPanel = (panel: any) => {
    if (panelName && panel?.panelName === panelName) return true;
    const currentPanelId = toComparableId(panel?.panelID);
    return panelIdStr !== undefined && currentPanelId === panelIdStr;
  };

  const hideFieldPanels = (sectionIndex: number, fieldName: string) => {
    if (sectionIndex === undefined || sectionIndex === null || sectionIndex === -1)
      return;
    inputsCopy[sectionIndex]?.fields?.forEach((field: any) => {
      if (field.systemFieldName !== fieldName) return;

      // Codes owned by the panel being deselected. Needed because prefilled
      // rows (edit/copy flows) can arrive without a panelID, so filtering by
      // panelID alone would leave them behind in the Diagnosis Code(s) list.
      const removableCodes = new Set<string>();
      const collectCode = (code: any) => {
        const normalised = toComparableId(code);
        if (normalised) removableCodes.add(normalised);
      };

      field?.panels?.forEach((panel: any) => {
        if (!isTargetPanel(panel)) return;
        panel.isVisible = false;
        panel.isSelected = false;
        panel.testOptions?.forEach((opt: any) => {
          opt.isSelected = false;
          collectCode(opt?.testCode);
        });
        panel.icD10Group?.forEach((group: any) => {
          group.isSelected = false;
          collectCode(group?.icD10Code);
        });
        panel.comorbidityGroup?.forEach((group: any) => {
          group.isSelected = false;
          collectCode(group?.comorbidityCode);
        });
      });

      const defaultVal = parseIcdComorbidityDefaultValue(field.defaultValue);
      if (defaultVal.length > 0) {
        field.defaultValue = defaultVal.filter((item: any) => {
          const itemPanelId = toComparableId(item?.panelID);
          if (panelIdStr !== undefined && itemPanelId === panelIdStr)
            return false;
          const itemCode = toComparableId(item?.Code);
          return !(itemCode && removableCodes.has(itemCode));
        });
      }
    });
  };

  hideFieldPanels(getICDPanelsIndex(inputsCopy), "ICDPanels");
  hideFieldPanels(getComorbiditiesPanelsIndex(inputsCopy), "ComorbidityPanels");
};

/**
 * Pre-fills the Diagnosis / ICD-10 section of a freshly loaded
 * LoadReqSection/ByFacilityIDandInsuranceId response with the diagnosis codes
 * the patient smart search returned (`patientDignosisCodes`).
 *
 * The requisition sections do not exist when the patient is selected, so the
 * codes are parked in Redux and applied here, once the sections are known. A
 * code is only applied to the requisition whose `reqId` matches its
 * `reqTypeId`; codes that match no returned requisition are dropped.
 *
 * Mutates `reqSections` in place and returns how many codes were applied.
 */
export const applyPendingDiagnosisCodes = (
  reqSections: any,
  pendingDiagnosisCodes: any
) => {
  if (!Array.isArray(reqSections) || !Array.isArray(pendingDiagnosisCodes)) {
    return 0;
  }
  if (!pendingDiagnosisCodes.length) return 0;

  let appliedCount = 0;

  reqSections.forEach((requisition: any) => {
    const codesForReq = pendingDiagnosisCodes.filter(
      (code: any) =>
        code?.icD10Code !== null &&
        code?.icD10Code !== undefined &&
        code?.reqTypeId?.toString() === requisition?.reqId?.toString()
    );
    if (!codesForReq.length) return;

    requisition?.sections?.forEach((section: any) => {
      section?.fields?.forEach((field: any) => {
        if (field?.systemFieldName !== "ICDPanels") return;

        const diagnosisCodes = parseIcdComorbidityDefaultValue(
          field.defaultValue
        );

        codesForReq.forEach((code: any) => {
          const codeStr = code.icD10Code.toString();
          const alreadyAdded = diagnosisCodes.some(
            (item: any) => item?.Code?.toString() === codeStr
          );
          if (alreadyAdded) return;

          // Mirror the pre-fill onto the panel checkbox the code belongs to, so
          // the table row and the checkbox stay in sync and removing the row
          // deselects it again (Icd10Section matches on Code + panelID).
          let panelID: any;
          field?.panels?.forEach((panel: any) => {
            panel?.icD10Group?.forEach((group: any) => {
              if (group?.icD10Code?.toString() !== codeStr) return;
              group.isSelected = true;
              panelID = panel?.panelID;
            });
          });

          diagnosisCodes.push({
            Code: code.icD10Code,
            Description: code.icD10CodeDescription ?? "",
            ...(panelID !== undefined ? { panelID } : {}),
          });
          appliedCount++;
        });

        field.defaultValue = diagnosisCodes;
      });
    });
  });

  return appliedCount;
};

export const processRequisitionSectionResponse = (data: any[]): any[] => {
  if (!Array.isArray(data)) return [];
  data.forEach((requisitionNameLevel: any) => {
    requisitionNameLevel?.sections?.forEach((sectionsLevel: any) => {
      sectionsLevel?.fields?.forEach((fieldsLevel: any) => {
        fieldsLevel.singleRequsition = true;
        if (fieldsLevel.defaultValue) {
          fieldsLevel.visible = true;
        }
        if (Array.isArray(fieldsLevel.panels)) {
          fieldsLevel.panels.forEach((panel: any) => {
            if (Array.isArray(panel?.testOptions)) {
              panel.testOptions.sort((a: any, b: any) => {
                const subDiff =
                  (a.subPanelSortOrder ?? 0) - (b.subPanelSortOrder ?? 0);
                return subDiff !== 0
                  ? subDiff
                  : (a.testSortOrder ?? 0) - (b.testSortOrder ?? 0);
              });
            }
          });
        }
      });
    });
  });
  return data;
};

export const mergeExpandedRequisitionIntoData = (
  existingData: any[],
  expandedData: any[],
  reqTypeId: number
): any[] => {
  return existingData.map((req: any) => {
    if (req.reqId?.toString() !== reqTypeId?.toString()) return req;
    const expanded = Array.isArray(expandedData)
      ? expandedData.find(
          (r: any) => r.reqId?.toString() === reqTypeId?.toString()
        )
      : undefined;
    if (!expanded) return req;
    return { ...req, ...expanded, isSelected: req.isSelected };
  });
};

export const expandSelectedRequisitions = async (
  baseRequest: any,
  baseData: any[],
  apiCall: (obj: any) => Promise<any>
): Promise<any[]> => {
  const selected = baseData.filter((req: any) => req.isSelected);
  if (!selected.length) return baseData;

  let result = [...baseData];
  for (const req of selected) {
    const expandRequest = {
      ...baseRequest,
      isRequisitionExpand: true,
      reqTypeId: req.reqId,
    };
    try {
      const res = await apiCall(expandRequest);
      processRequisitionSectionResponse(res.data);
      result = mergeExpandedRequisitionIntoData(result, res.data, req.reqId);
    } catch (e) {
      console.error("Failed to expand requisition", req.reqId, e);
    }
  }
  return result;
};

export const getLoadReqSectionTypeId = (reqConfig: any): number => {
  return reqConfig?.reqId;
};

export const getToxTestingOption = (inputsCopy: any) => {
  let index = inputsCopy.findIndex((i: any) => i.sectionId == 17);
  return index;
};
export const getToxCompendiumIndex = (arr: any) => {
  let index = arr.findIndex((i: any) => i.systemFieldName === "Compendium");
  return index;
};
export const getToxReflexToConfirmationIndex = (inputsCopy: any) => {
  let index = inputsCopy.findIndex((i: any) => i.sectionId == 35);
  return index;
};
export const upsertArray = (array: any[], element: any, condition: any) => {
  // (1)
  //;

  const i: any = array?.findIndex(condition);

  if (i > -1) array[i] = element; // (2)
  else array.push(element);
  return array;
};

export const assignValuesForSearchInputs = (
  array: any[],
  tabsDetail: any,
  value: any
) => {
  return "";
};

export const emptyObjectValues = (searchValue: any) => {
  Object.keys(searchValue).forEach(function (index) {
    searchValue[index] = "";
  });

  return searchValue;
};

export const getTotalPagesCount = (pageSize: any, totalPageCount: any) => {
  let totalCount = totalPageCount / pageSize;
  let totalCountToInteger = Math.ceil(totalCount);
  return totalCountToInteger;
};
export const setDateToInput = (filterData: any, columnKey?: string) => {
  let filterDate = filterData?.filters?.find(
    (filteredObj: any) => filteredObj.columnKey == columnKey
  );

  if (filterDate?.columnKey == columnKey) {
    return filterDate?.filterValue;
  } else {
    return "";
  }
};

export const modifiedInputsWithSignature = (apiResCopy: any) => {
  apiResCopy.forEach((inputs: any) => {
    if (inputs.sectionName === "Physician Signature") {
      inputs.fields.forEach((fields: any) => {
        if (
          fields.systemFieldName == "PhysicianSignature" &&
          fields.defaultValue
        ) {
          fields.signatureText = fields.defaultValue;
        }
      });
    }
  });
  return apiResCopy;
};

// ** Panel IDs that cannot be selected togeather */
// MY CODE START
export const computeDisabledPanelIds = (
  panels: any[] = [],
  panelCombinations: any[] = []
): Set<string> => {
  const disabled = new Set<string>();
  const selectedIds = new Set(
    panels
      .filter((panel: any) => panel.isSelected)
      .map((panel: any) => String(panel.panelID))
  );

  panelCombinations.forEach((combo: any) => {
    if (combo.mainPanelId == null) return;

    let restrictedPanels: any[];
    try {
      restrictedPanels = JSON.parse(combo.panelCombinationJson || "[]");
    } catch {
      return;
    }

    if (!Array.isArray(restrictedPanels)) return;

    const mainId = String(combo.mainPanelId);

    restrictedPanels.forEach((panel: any) => {
      if (panel?.PanelId == null) return;

      const restrictedId = String(panel.PanelId);
      if (restrictedId === mainId) return;

      // Main selected: disable the restricted panel.
      if (selectedIds.has(mainId) && !selectedIds.has(restrictedId)) {
        disabled.add(restrictedId);
      }

      // Restricted selected: disable the main panel.
      if (selectedIds.has(restrictedId) && !selectedIds.has(mainId)) {
        disabled.add(mainId);
      }
    });
  });

  return disabled;
};

export const getConflictingPanelNameForCombination = (
  panelID: string | number,
  panels: any[] = [],
  panelCombinations: any[] = []
): string => {
  const candidateId = String(panelID);

  for (const combo of panelCombinations) {
    if (combo.mainPanelId == null) continue;

    let restrictedPanels: any[];
    try {
      restrictedPanels = JSON.parse(combo.panelCombinationJson || "[]");
    } catch {
      continue;
    }

    if (!Array.isArray(restrictedPanels)) continue;

    const mainId = String(combo.mainPanelId);
    const restrictedIds = restrictedPanels
      .filter((panel: any) => panel?.PanelId != null)
      .map((panel: any) => String(panel.PanelId))
      .filter((id: string) => id !== mainId);

    const conflict = panels.find((panel: any) => {
      if (!panel.isSelected) return false;

      const selectedId = String(panel.panelID);
      if (selectedId === candidateId) return false;

      return (
        (candidateId === mainId && restrictedIds.includes(selectedId)) ||
        (restrictedIds.includes(candidateId) && selectedId === mainId)
      );
    });

    if (conflict) return conflict.panelName ?? "";
  }

  return "";
};